import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Divider, IconButton, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useCart } from '../../contexts/CartContext';
import useCompanyName from '../../hooks/useCompanyName';
import { PageHeader, RowThumb, QuantityStepper, ConfirmDialog, ErrorState, EmptyState } from '../ui';
import { formatCurrency } from '../../utils/currencyFormatter';
import { radius, layout } from '../../themes/smartinventoryTheme';

const Cart = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const companyName = useCompanyName();
  const {
    cart, loading, error, updateQuantity, removeItem, clearCart, updateMessage, checkout,
  } = useCart();

  const [message, setMessage] = useState(cart?.message || '');
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [confirmCheckoutOpen, setConfirmCheckoutOpen] = useState(false);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [placedOrders, setPlacedOrders] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);

  const handleQuantityChange = (productId, newQuantity) => {
    updateQuantity(productId, newQuantity);
  };

  const handleClearCart = () => {
    setConfirmClearOpen(false);
    clearCart();
  };

  const handleCheckout = async () => {
    setCheckingOut(true);
    const result = await checkout();
    setCheckingOut(false);
    setConfirmCheckoutOpen(false);
    if (result.success) {
      setPlacedOrders(result.orders);
      setCheckoutDialogOpen(true);
    }
  };

  const handleCloseCheckoutDialog = () => {
    setCheckoutDialogOpen(false);
    navigate('/my-orders');
  };

  const getAppliedCouponsWithSavings = () => {
    if (!cart?.items) return [];
    const couponMap = new Map();
    cart.items.forEach((item) => {
      (item.appliedCoupons || []).forEach((coupon) => {
        const key = coupon.code || coupon._id;
        if (!couponMap.has(key)) {
          couponMap.set(key, { ...coupon, totalSavings: coupon.savings || 0 });
        } else {
          couponMap.get(key).totalSavings += (coupon.savings || 0);
        }
      });
    });
    return Array.from(couponMap.values());
  };

  const appliedCoupons = getAppliedCouponsWithSavings();
  // The order goes to the company this user belongs to (their supplier);
  // fall back to a generic noun until the name has loaded.
  const supplierName = companyName || t('your supplier', { defaultValue: 'your supplier' });
  const hasDiscounts = cart?.subtotalBeforeDiscounts && cart?.subtotalBeforeDiscounts !== cart?.totalPrice;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return (
      <ErrorState
        title={t("Couldn't load your cart", { defaultValue: "Couldn't load your cart" })}
        description={error}
      />
    );
  }

  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <EmptyState
        kind="none"
        icon={ShoppingCartIcon}
        title={t('Your cart is empty')}
        description={t('Products you add to your cart will show up here.', { defaultValue: 'Products you add to your cart will show up here.' })}
        actions={[{ label: t('Continue Shopping'), variant: 'contained', onClick: () => navigate('/shop') }]}
      />
    );
  }

  return (
    <Box sx={{ pb: { xs: 9, sm: 0 } }}>
      <PageHeader
        title={t('Your cart', { defaultValue: 'Your cart' })}
        actions={[{ label: t('Continue Shopping'), icon: ArrowBackIcon, variant: 'outlined', onClick: () => navigate('/shop') }]}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined">
            {cart.items.map((item, index) => (
              <Box key={item.productId} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, borderTop: index > 0 ? '1px solid' : 'none', borderColor: 'divider', flexWrap: 'wrap' }}>
                <RowThumb src={item.productImage} name={item.productName} />
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{item.productName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(item.unitPrice || 0)} {t('per')} {item.unit}
                  </Typography>
                  {item.appliedCoupons?.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      {item.appliedCoupons.map((coupon) => (
                        <Typography key={coupon.code} variant="caption" sx={{ color: 'success.main', display: 'block' }}>
                          {coupon.code} {t('applied')}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
                <QuantityStepper
                  value={item.quantity}
                  min={1}
                  max={typeof item.availableQuantity === 'number' ? item.availableQuantity : undefined}
                  onChange={(next) => handleQuantityChange(item.productId, next)}
                />
                <Typography variant="body2" fontWeight={700} sx={{ width: 90, textAlign: 'right' }}>
                  {formatCurrency(item.totalPrice ?? ((item.unitPrice || 0) * (item.quantity || 1)))}
                </Typography>
                <IconButton color="error" onClick={() => removeItem(item.productId)} aria-label={t('Remove')}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5 }}>
              <Button variant="text" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmClearOpen(true)}>
                {t('Clear Cart')}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{t('Order Summary')}</Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2" color="text.secondary">{t('Items')} ({cart.itemCount})</Typography>
              <Typography variant="body2">{formatCurrency(hasDiscounts ? cart.subtotalBeforeDiscounts : cart.totalPrice)}</Typography>
            </Box>

            {hasDiscounts && appliedCoupons.map((coupon) => (
              <Box key={coupon.code} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                  <LocalOfferIcon fontSize="small" />
                  <Typography variant="body2" color="success.main">{coupon.code}</Typography>
                </Box>
                <Typography variant="body2" color="success.main" fontWeight={700}>
                  −{formatCurrency(coupon.totalSavings)}
                </Typography>
              </Box>
            ))}

            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1">{t('Total')}</Typography>
              <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(cart.totalPrice)}</Typography>
            </Box>
            {hasDiscounts && (
              <Typography variant="caption" color="success.main" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
                {t('You saved {{amount}}', { amount: formatCurrency(cart.totalDiscount || (cart.subtotalBeforeDiscounts - cart.totalPrice)), defaultValue: `You saved ${formatCurrency(cart.totalDiscount || (cart.subtotalBeforeDiscounts - cart.totalPrice))}` })}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('Tax is not included in this total. Your supplier will add the applicable tax and send you an invoice.', { defaultValue: 'Tax is not included in this total. Your supplier will add the applicable tax and send you an invoice.' })}
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2.5, mb: 0.5 }}>
              {t('Message for your order', { defaultValue: 'Message for your order' })}
            </Typography>
            <TextField
              fullWidth multiline rows={2} size="small"
              placeholder={t('Add a message for your order (optional)')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onBlur={() => updateMessage(message)}
            />

            <Button
              variant="contained" fullWidth size="large" sx={{ mt: 2.5, display: { xs: 'none', sm: 'inline-flex' } }}
              startIcon={checkingOut ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
              onClick={() => setConfirmCheckoutOpen(true)}
              disabled={checkingOut}
            >
              {t('Checkout')} · {formatCurrency(cart.totalPrice)}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          position: 'fixed', left: 0, right: 0, bottom: layout.bottomBarHeight, zIndex: (theme) => theme.zIndex.appBar,
          px: 2, py: 1.5, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider',
          alignItems: 'center', gap: 1.5,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">{t('Total')}</Typography>
          <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(cart.totalPrice)}</Typography>
        </Box>
        <Button variant="contained" onClick={() => setConfirmCheckoutOpen(true)} disabled={checkingOut}>
          {checkingOut ? <CircularProgress size={18} color="inherit" /> : t('Checkout')}
        </Button>
      </Box>

      <ConfirmDialog
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        tone="danger"
        icon={DeleteIcon}
        title={t('Clear Shopping Cart')}
        description={t('Are you sure you want to remove all items from your cart? This action cannot be undone.')}
        cancelLabel={t('Cancel')}
        confirmLabel={t('Clear Cart')}
        onConfirm={handleClearCart}
      />

      <ConfirmDialog
        open={confirmCheckoutOpen}
        onClose={() => { if (!checkingOut) setConfirmCheckoutOpen(false); }}
        tone="primary"
        icon={CheckCircleIcon}
        title={t('Submit order to {{company}}?', { company: supplierName, defaultValue: `Submit order to ${supplierName}?` })}
        description={t('You are about to send this order for {{amount}} to {{company}}. They will review it, add the applicable tax, and send you an invoice. Please confirm to continue.', {
          amount: formatCurrency(cart.totalPrice),
          company: supplierName,
          defaultValue: `You are about to send this order for ${formatCurrency(cart.totalPrice)} to ${supplierName}. They will review it, add the applicable tax, and send you an invoice. Please confirm to continue.`,
        })}
        cancelLabel={t('Go back', { defaultValue: 'Go back' })}
        confirmLabel={t('Submit order', { defaultValue: 'Submit order' })}
        onConfirm={handleCheckout}
        loading={checkingOut}
      />

      <Dialog open={checkoutDialogOpen} onClose={handleCloseCheckoutDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CheckCircleIcon color="success" />
          {t('Order Placed Successfully')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('Your order has been placed and is being processed.')}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('Product')}</TableCell>
                  <TableCell align="center">{t('Quantity')}</TableCell>
                  <TableCell align="right">{t('Total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {placedOrders?.map((order) => (
                  <TableRow key={order._id}>
                    <TableCell>{order.productName}</TableCell>
                    <TableCell align="center">{order.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(order.totalPrice)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseCheckoutDialog} autoFocus sx={{ borderRadius: `${radius.pill}px` }}>
            {t('View My Orders')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cart;
